<template>
  <div id="menu" :class="{'menu-container': true, open: open}">
    <navigation-header :open="open" @toggle-menu="toggleMenu" />
    <div class="menu-content">
      <navigation-list
        class="navigation"
        :sections="sections"
        :active="active"
        :visited="visited"
        @select="select($event)" />
      <navigation-footer class="footer" />
    </div>
  </div>
</template>

<script>
import NavigationHeader from './Header.vue'
import NavigationFooter from './Footer.vue'
import NavigationList   from './NavigationList.vue'

export default {
  name: 'navigation',
  components: {
    'navigation-header': NavigationHeader,
    'navigation-list': NavigationList,
    'navigation-footer': NavigationFooter,
  },
  data() {
    return {
      open: false
    }
  },
  props: {
    sections: Array,
    active: String,
    visited: Array
  },
  methods: {
    select() {
      this.toggleMenu()
    },
    toggleMenu() {
      this.open = !this.open
      if (this.open) {
        document.body.classList.add('menu-open')
      } else {
        document.body.classList.remove('menu-open')
      }
      this.$emit('menu-toggle', this.open)
    }
  }
}
</script>

<style lang="scss">
/** navigation (unscoped) **/
@import '../../theme';
@import './variables';

body.menu-open {
  overflow: hidden !important;
  @include media-breakpoint-up(lg) {
    overflow: scroll !important;
  }
}

// Unscoped so we can target sub-component styles.
#menu {
  @include media-breakpoint-up(lg) {
    transition: min-width $t-m $e-out-hard $t-s;

    .navigation {
      overflow: hidden;
      left: 0;
      right: 0;
      transform: translateX(0);
    }

    .nav-links {
      $center: ($nav-width - $dot-l - ($nav-border-thickness * 2) - $nav-padding)/2;
      transform: translateX(#{$center});
      transition: transform $t-m $e-out-hard $t-s;
    }

    .title {
      opacity: 0;
      transition: opacity $t-s linear 0s;
    }

    &:hover {
      $open-width: $nav-width * 1.4;
      // Make the nav grow 40%
      min-width: $open-width;
      transition: min-width $t-m $e-out-hard 0s;

      .nav-links {
        // Unfortunately this can't be calculated so hard coding it here.
        $bullets-and-text-width: 164;
        $center: ($open-width - $bullets-and-text-width - $nav-padding) / 2;
        transform: translateX(#{$center});
        transition: transform $t-m $e-out-hard 0s;
      }

      .title {
        opacity: 1;
        transition: opacity $t-m linear $t-s;
      }
    }
  }
}

// Turn off the navigation background image because it
// has trouble rendering that SVG.
body.ie #menu .menu-content:before {
  background-image: none;
}

// Turn off navigation background transitions in Firefox
// because a rendering bug causes the page to glitch like a bad tv.
body.firefox #menu .menu-content:before {
  transition: none;
}
</style>

<style scoped lang="scss">
/** navigation **/
@import '../../theme';
@import './variables';

.menu-container {
  z-index: zespri-layer(navigation);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100vw;
  height: $nav-height;

  display: flex;
  flex-direction: column;
  align-items: center;

  transition: height 0s linear $t-l;

  @include media-breakpoint-up(lg) {
    right: auto;
    bottom: 0;
    min-width: $nav-width;
    width: $nav-width;
    height: 100vh;
    transition-delay: 0s;
    background-color: zespri-brand-color(yellow);
  }
}

.header {
  flex-grow: 0;
  z-index: zespri-layer(header);

  background-color: zespri-brand-color(yellow);
  transition: background-color $t-s linear;

  @include media-breakpoint-up(lg) {
    background-color: transparent;
  }
}

.menu-content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  align-items: center;
  width: 100%;
  @include absolute-fill;
  overflow: hidden;
  transform: translateY(-100%);

  transition: transform $t-m $e-out-hard;
  background-color: zespri-brand-color(yellow);

  &:before {
    content: '';
    position: absolute;
    top: $nav-height * -1;
    bottom: 0;
    right: 0;
    left: 0;

    background-image: url('../../assets/nav-background.svg');
    background-color: zespri-brand-color(yellow);
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    opacity: 0;
    transform: scale(1.4) rotate(20deg);

    transition: transform $t-xl $e-out-hard $t-s, opacity $t-s linear;
  }

  @include media-breakpoint-up(lg) {
    transform: translateY(0);

    &:before {
      opacity: 1;
      transform: scale(1) rotate(0);
    }
  }
}

.navigation {
  max-width: 350px;
  display: flex;
  flex-grow: 1;
  align-items: center;
  position: absolute;
  top: 0;
  // Extra padding on mobile to account for iOS nav buttons.
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);

  z-index: zespri-layer(normal);
  opacity: 0;
  transition: opacity 0s linear 0s;

  @include media-breakpoint-up(lg) {
    opacity: 1;
    bottom: 0;
  }
}

.footer {
  @include absolute-bottom-center;
  flex-grow: 0;
  z-index: zespri-layer(normal);
  opacity: 0;
  transition: opacity 0s linear 0s;

  @include media-breakpoint-up(lg) {
    opacity: 1;
  }
}

.open {
  &.menu-container {
    height: 100vh;
    transition-delay: 0s;
  }

  .header {
    background-color: transparent;
  }

  .menu-content {
    transform: translateY(0);

    &:before {
      opacity: 1;
      transform: scale(1.2) rotate(0);
    }
  }

  .navigation, .footer {
    transition: opacity $t-m linear $t-s;
    opacity: 1;
  }
}
</style>
